(() => {
  const rootSelector = '[data-gp-game-pdp]';

  const readJson = (root, selector) => {
    const node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  };

  const setupGalleryHud = (root) => {
    const gallery = root.querySelector('[data-gp-product-gallery]');
    if (!gallery || gallery.dataset.gpGameReady === 'true') return;
    gallery.dataset.gpGameReady = 'true';

    const dots = [...gallery.querySelectorAll('[data-gp-gallery-dot]')];
    const counter = gallery.querySelector('[data-gp-gallery-current]');
    const frame = gallery.querySelector('.gp-product-gallery__frame');
    let flickerTimer = 0;

    const sync = () => {
      const activeIndex = Math.max(0, dots.findIndex((dot) => dot.getAttribute('aria-current') === 'true'));
      if (counter) counter.textContent = String(activeIndex + 1).padStart(2, '0');
      const activeDot = dots[activeIndex];
      const strip = activeDot?.parentElement;
      if (activeDot && strip) {
        const targetLeft = activeDot.offsetLeft - (strip.clientWidth - activeDot.offsetWidth) / 2;
        strip.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      if (!frame || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      window.clearTimeout(flickerTimer);
      frame.classList.remove('is-scanning');
      window.requestAnimationFrame(() => frame.classList.add('is-scanning'));
      flickerTimer = window.setTimeout(() => frame.classList.remove('is-scanning'), 170);
    };

    dots.forEach((dot) => {
      new MutationObserver(sync).observe(dot, { attributes: true, attributeFilter: ['aria-current'] });
    });

    sync();
  };

  const setupVariantHud = (root) => {
    const form = root.querySelector('.gp-product-form');
    if (!form || form.dataset.gpGameVariantReady === 'true') return;
    form.dataset.gpGameVariantReady = 'true';

    const product = readJson(root, '[data-gp-product-json]');
    const priceMap = readJson(root, '[data-gp-variant-price-map]') || {};
    const idInput = form.querySelector('input[name="id"]');
    const selects = [...form.querySelectorAll('[data-gp-option-select]')];
    const buttons = [...form.querySelectorAll('[data-gp-option-button]')];
    const price = root.querySelector('[data-gp-product-price]');
    const variantReadouts = document.querySelectorAll('[data-gp-active-variant]');
    const availabilityReadouts = document.querySelectorAll('[data-gp-availability], [data-gp-lower-availability]');

    if (!product || !idInput) return;

    const selectedOptions = () => selects.map((select) => select.value);

    const findVariant = (options) => {
      if (!Array.isArray(product.variants)) return null;
      if (options.length === 0) {
        return product.variants.find((variant) => String(variant.id) === String(idInput.value)) || product.variants[0];
      }
      return product.variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    };

    const syncAvailability = (options) => {
      buttons.forEach((button) => {
        const optionIndex = Number(button.dataset.optionIndex);
        const candidateOptions = [...options];
        candidateOptions[optionIndex] = button.dataset.optionValue;
        const canResolve = product.variants.some((variant) => {
          return variant.available && variant.options.every((option, index) => option === candidateOptions[index]);
        });
        button.disabled = !canResolve;
        button.setAttribute('aria-disabled', canResolve ? 'false' : 'true');
      });
    };

    const sync = () => {
      const options = selectedOptions();
      const variant = findVariant(options);
      if (!variant) return;

      if (price && priceMap[String(variant.id)]) price.textContent = priceMap[String(variant.id)];
      variantReadouts.forEach((node) => { node.textContent = variant.id; });
      availabilityReadouts.forEach((node) => { node.textContent = variant.available ? 'AVAILABLE' : 'UNAVAILABLE'; });
      syncAvailability(options);
    };

    selects.forEach((select) => select.addEventListener('change', () => window.requestAnimationFrame(sync)));
    sync();
  };

  const setupProductRating = (root) => {
    const rating = root.querySelector('.gp-product-rating');
    if (!rating || rating.dataset.gpRatingReady === 'true') return;
    rating.dataset.gpRatingReady = 'true';

    rating.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = document.querySelector('#judgeme_product_reviews');
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();

      const pageWrapper = target.closest('.page-wrapper');
      const scrollContainer = pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight
        ? pageWrapper
        : document.scrollingElement;
      const currentScroll = scrollContainer === document.scrollingElement ? window.scrollY : scrollContainer.scrollTop;
      const containerTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
      const promoHeight = Number.parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--gp-promo-height'),
      ) || 0;
      const targetTop = target.getBoundingClientRect().top - containerTop + currentScroll - promoHeight - 16;

      const scrollOptions = {
        top: Math.max(0, targetTop),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      };
      if (scrollContainer === document.scrollingElement) {
        window.scrollTo(scrollOptions);
      } else {
        scrollContainer.scrollTo(scrollOptions);
      }
      window.history.replaceState(null, '', rating.getAttribute('href'));
    }, { capture: true });
  };

  const setupRelatedControls = (scope = document) => {
    const dashboards = scope.matches?.('[data-gp-lower-dashboard]')
      ? [scope]
      : [...scope.querySelectorAll('[data-gp-lower-dashboard]')];
    dashboards.forEach((dashboard) => {
      const track = dashboard.querySelector('[data-gp-related-track]');
      const previous = dashboard.querySelector('[data-gp-related-prev]');
      const next = dashboard.querySelector('[data-gp-related-next]');
      if (!track || track.dataset.gpControlsReady === 'true') return;
      track.dataset.gpControlsReady = 'true';

      const move = (direction) => {
        const card = track.querySelector('.gp-related-card');
        const distance = card ? card.getBoundingClientRect().width + 8 : track.clientWidth * 0.8;
        track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      };

      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
    });
  };

  let reassuranceObserver;

  const placeCheckoutReassurance = () => {
    const root = document.querySelector(rootSelector);
    const lowerDashboard = document.querySelector('[data-gp-lower-dashboard]');
    if (!root || !lowerDashboard) return false;

    const placedBanner = document.querySelector('.gp-checkout-reassurance');
    if (placedBanner) {
      lowerDashboard.parentElement?.insertBefore(placedBanner, lowerDashboard);
      return true;
    }

    const banner = [...root.querySelectorAll('[data-banners]')].find((node) => {
      const text = node.textContent?.replace(/\s+/g, ' ').trim() || '';
      return text.includes('Free Shipping') && text.includes('Trusted Payment Options');
    });
    if (!banner) return false;

    banner.classList.add('gp-checkout-reassurance');
    lowerDashboard.parentElement?.insertBefore(banner, lowerDashboard);
    return true;
  };

  const setupCheckoutReassurance = () => {
    if (placeCheckoutReassurance()) {
      reassuranceObserver?.disconnect();
      reassuranceObserver = undefined;
      return;
    }
    if (reassuranceObserver || !document.body) return;

    reassuranceObserver = new MutationObserver(() => {
      if (!placeCheckoutReassurance()) return;
      reassuranceObserver?.disconnect();
      reassuranceObserver = undefined;
    });
    reassuranceObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (!customElements.get('gp-related-evidence')) {
    customElements.define('gp-related-evidence', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true' || !this.dataset.url) return;
        this.dataset.loaded = 'true';

        fetch(this.dataset.url, { headers: { Accept: 'text/html' } })
          .then((response) => {
            if (!response.ok) throw new Error('Recommendations request failed');
            return response.text();
          })
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const incoming = parsed.querySelector('[data-gp-related-results]');
            const current = this.querySelector('[data-gp-related-results]');
            if (!incoming || !current) return;
            current.replaceWith(incoming);
            setupRelatedControls(this.closest('[data-gp-lower-dashboard]') || document);
          })
          .catch(() => {
            const current = this.querySelector('[data-gp-related-results]');
            if (current) current.innerHTML = '<p class="gp-related-evidence__empty">RELATED EVIDENCE IS TEMPORARILY OFFLINE.</p>';
          });
      }
    });
  }

  const init = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach((root) => {
      setupGalleryHud(root);
      setupVariantHud(root);
      setupProductRating(root);
    });
    setupRelatedControls(scope);
    setupCheckoutReassurance();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
